import httpStatus, { type HttpStatus } from 'http-status';
import getT from 'next-translate/getT';
import { toast } from 'react-toastify';
import { isRejectedWithValue, Middleware } from '@reduxjs/toolkit';
import { appInsights } from '@/telemetry/client';
import type { TypeOptions } from 'react-toastify';

async function getStaticTranslation({ locale = 'nl-NL', key }: { locale?: string; key: string }): Promise<string> {
  const t = await getT(locale, ['common', 'form-errors']);
  return t(key);
}

export const rtkQueryErrorToastMessage: Middleware = () => (next) => async (action) => {
  if (!action?.payload) {
    next(action);
    return;
  }

  if (httpStatus[`${action.payload.status}_CLASS` as keyof HttpStatus] === httpStatus.classes.SERVER_ERROR) {
    if (httpStatus?.classes?.SERVER_ERROR) {
      /* eslint-disable max-depth */
      try {
        const errorMessage = JSON.stringify(action, null, 2);
        appInsights.trackException({ exception: new Error(errorMessage) });
      } catch (e) {
        const error = new Error('Unknown Rejected Action', { cause: e });
        appInsights.trackException({ exception: error });
      }
    }
  }

  const severity: string = action.payload.data?.severity ?? action.payload.severity ?? '';
  const title: string = action.payload.data?.title ?? action.payload.title ?? '';
  const isApiMessage: boolean = !!title && !!severity;

  // Recoverable error
  if (isApiMessage) {
    const TypeOptionsArray = ['info', 'success', 'warning', 'error'] as TypeOptions[];
    const toastType = TypeOptionsArray.find((option) => option.toLocaleLowerCase().includes(severity.toLocaleLowerCase()));

    const toastTitle = await getStaticTranslation({ key: title });
    toast(toastTitle, {
      type: toastType,
      toastId: title
    });
    return next(action);
  }

  // Fallback
  if (isRejectedWithValue(action)) {
    let errorMessage = '';

    switch (action?.payload.status) {
      case 400:
        // 400 indicates a validation error. Those are handled by the component it self
        return next(action);

      case 401:
        errorMessage = 'Je moet ingelogd zijn om deze actie uit te voeren.';
        break;
      case 403:
        errorMessage = 'Je hebt momenteel geen rechten om deze actie uit te voeren.';
        break;
      case 404:
        errorMessage = '404: The resource cannot be found';
        break;
      case 405:
        errorMessage = '405: Method Not Allowed';
        break;
      case 409:
        errorMessage = '409: Actie kan niet worden voltooid: Gegevensconflict gedetecteerd.';
        break;
      case 500:
        errorMessage = 'Er is iets fout gegaan. Probeer het later nog een keer';
        break;
      case 502:
        errorMessage = 'Er is een netwerk storing. Probeer het later nog een keer';
        break;
      case 503:
        errorMessage = 'Er is een netwerk storing. Probeer het later nog een keer';
        break;
      default:
        errorMessage = 'Er is iets fout gegaan. Probeer het later nog een keer';
        break;
    }

    toast(errorMessage, {
      type: 'error',
      toastId: errorMessage
    });
  }

  return next(action);
};
